import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, DollarSign, RotateCcw, Send } from '../lib/icons';
import SEOHead from '../components/SEOHead';
import {
  BUYBACK_CATEGORIES,
  getCategory,
  getModel,
  calculateOffer,
  CONDITIONS,
  type Condition,
} from '../data/buyback-pricing';

type Step = 'category' | 'model' | 'condition' | 'offer';

const STEPS: Step[] = ['category', 'model', 'condition', 'offer'];

const STEP_LABELS = ['Device', 'Model', 'Condition', 'Your Offer'];

// ── Step indicator ────────────────────────────────────────────────────────────
const StepBar = ({ current }: { current: Step }) => {
  const idx = STEPS.indexOf(current);
  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                i < idx
                  ? 'bg-[#39FF14] text-[#0D0D0D]'
                  : i === idx
                  ? 'bg-[#00BFFF] text-[#0D0D0D]'
                  : 'bg-gray-800 text-gray-500'
              }`}
            >
              {i < idx ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={`mt-1.5 text-xs font-medium hidden sm:block ${
                i === idx ? 'text-[#00BFFF]' : i < idx ? 'text-[#39FF14]' : 'text-gray-600'
              }`}
            >
              {label}
            </span>
          </div>
          {i < STEP_LABELS.length - 1 && (
            <div
              className={`h-0.5 w-12 sm:w-20 mx-1 transition-all duration-300 ${
                i < idx ? 'bg-[#39FF14]' : 'bg-gray-800'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const BuybackPage = () => {
  const [step, setStep] = useState<Step>('category');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [condition, setCondition] = useState<Condition | null>(null);
  const [iCloudUnlocked, setICloudUnlocked] = useState<boolean | null>(null);
  const [blacklistClean, setBlacklistClean] = useState<boolean | null>(null);

  // contact form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setStep('category');
    setCategoryId(null);
    setModelId(null);
    setCondition(null);
    setICloudUnlocked(null);
    setBlacklistClean(null);
    setName('');
    setPhone('');
    setNotes('');
  };

  const selectedCategory = categoryId ? getCategory(categoryId) : null;
  const selectedModel = categoryId && modelId ? getModel(categoryId, modelId) : null;
  const selectedCondition = CONDITIONS.find(c => c.id === condition) ?? null;

  const offer =
    categoryId && modelId && condition
      ? calculateOffer(categoryId, modelId, condition, iCloudUnlocked ?? undefined, blacklistClean ?? undefined)
      : null;

  const needsAccountCheck = selectedCategory?.needsAccountCheck ?? false;
  const needsCarrierCheck = selectedCategory?.needsCarrierCheck ?? false;

  const conditionComplete =
    condition !== null &&
    (!needsAccountCheck || iCloudUnlocked !== null) &&
    (!needsCarrierCheck || blacklistClean !== null);

  // mailto builder for the submit CTA
  const buildMailto = () => {
    const subject = encodeURIComponent(`Buyback Quote Request – ${selectedModel?.name ?? ''}`);
    const body = encodeURIComponent(
      `Hi GameFix,\n\nI'd like to sell my device.\n\nDevice: ${selectedModel?.name ?? ''}\nCondition: ${selectedCondition?.label ?? ''}\n${needsAccountCheck ? `iCloud/Account Unlocked: ${iCloudUnlocked ? 'Yes' : 'No'}\n` : ''}${needsCarrierCheck ? `Carrier/Blacklist Clean: ${blacklistClean ? 'Yes' : 'No'}\n` : ''}\nEstimated offer: $${offer}\n\nName: ${name}\nPhone/Contact: ${phone}\n${notes ? `Notes: ${notes}` : ''}`,
    );
    return `mailto:info@gamefixsolutions.com?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <SEOHead
        title="Sell Your Device | Instant Buyback Quotes – GameFix Solutions"
        description="Get an instant estimate for your iPhone, iPad, MacBook, PS5, Xbox, Nintendo Switch, Steam Deck, Game Boy, and more. Working or broken — we buy it all."
        keywords="sell ps5, sell iphone san diego, sell nintendo switch, device buyback temecula, sell broken iphone, sell game boy, sell macbook san diego"
        canonical="https://gamefixsolutions.com/buyback"
      />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-gradient-to-b from-[#0D0D0D] to-black">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-5 py-1.5 mb-6">
            <DollarSign className="h-4 w-4 text-[#39FF14]" />
            <span className="text-[#39FF14] font-semibold text-sm">INSTANT ESTIMATES</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
            What's Your Device Worth?
          </h1>
          <p className="text-lg text-[#B0B0B0] max-w-2xl mx-auto">
            Working or broken — get an instant estimate in under a minute. We buy iPhones, iPads, MacBooks, consoles, handhelds, and retro gear.
          </p>
        </div>
      </section>

      {/* Wizard */}
      <section className="py-10 bg-black min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-4">
          <StepBar current={step} />

          {/* ── Step 1: Category ─────────────────────────────────────────── */}
          {step === 'category' && (
            <div>
              <h2 className="text-xl font-bold text-[#E0E0E0] mb-6 text-center">What type of device are you selling?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {BUYBACK_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCategoryId(cat.id);
                      setModelId(null);
                      setCondition(null);
                      setICloudUnlocked(null);
                      setBlacklistClean(null);
                      setStep('model');
                    }}
                    className="flex flex-col items-center justify-center gap-2 p-5 rounded-2xl bg-gray-900 border border-gray-800 hover:border-[#00BFFF]/60 hover:bg-gray-800 transition-all group"
                  >
                    <span className="text-3xl">{cat.emoji}</span>
                    <span className="text-sm font-semibold text-[#E0E0E0] group-hover:text-[#00BFFF] transition-colors text-center leading-tight">{cat.name}</span>
                    <span className="text-xs text-gray-500 text-center leading-tight">{cat.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Model ─────────────────────────────────────────────── */}
          {step === 'model' && selectedCategory && (
            <div>
              <h2 className="text-xl font-bold text-[#E0E0E0] mb-2 text-center">
                Which {selectedCategory.name}?
              </h2>
              <p className="text-[#B0B0B0] text-sm text-center mb-6">Select your exact model for an accurate estimate</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto pr-1 scrollbar-thin">
                {selectedCategory.models.map(model => (
                  <button
                    key={model.id}
                    onClick={() => {
                      setModelId(model.id);
                      setCondition(null);
                      setICloudUnlocked(null);
                      setBlacklistClean(null);
                      setStep('condition');
                    }}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-900 border border-gray-800 hover:border-[#00BFFF]/60 hover:bg-gray-800 transition-all text-left group"
                  >
                    <span className="text-sm font-medium text-[#E0E0E0] group-hover:text-[#00BFFF] transition-colors">{model.name}</span>
                    <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">up to ${model.goodPrice}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep('category')}
                className="mt-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors mx-auto"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            </div>
          )}

          {/* ── Step 3: Condition ─────────────────────────────────────────── */}
          {step === 'condition' && selectedModel && (
            <div>
              <h2 className="text-xl font-bold text-[#E0E0E0] mb-2 text-center">
                What condition is your {selectedModel.name}?
              </h2>
              <p className="text-[#B0B0B0] text-sm text-center mb-6">Be honest — final offer confirmed in person or after photos</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {CONDITIONS.map(cond => (
                  <button
                    key={cond.id}
                    onClick={() => setCondition(cond.id)}
                    className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                      condition === cond.id
                        ? 'bg-gray-800'
                        : 'bg-gray-900 border-gray-800 hover:bg-gray-800'
                    }`}
                    style={{ borderColor: condition === cond.id ? cond.color : undefined }}
                  >
                    {condition === cond.id && (
                      <div
                        className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: cond.color }}
                      >
                        <Check className="h-3 w-3 text-[#0D0D0D]" />
                      </div>
                    )}
                    <p className="font-bold text-[#E0E0E0] mb-1" style={{ color: condition === cond.id ? cond.color : undefined }}>
                      {cond.label}
                    </p>
                    <p className="text-xs text-[#B0B0B0] leading-relaxed">{cond.desc}</p>
                  </button>
                ))}
              </div>

              {/* Apple-specific questions */}
              {needsAccountCheck && (
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 mb-4">
                  <p className="text-sm font-semibold text-[#E0E0E0] mb-3">
                    Is it signed out of iCloud / Apple ID?
                  </p>
                  <div className="flex gap-3">
                    {[true, false].map(val => (
                      <button
                        key={String(val)}
                        onClick={() => setICloudUnlocked(val)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                          iCloudUnlocked === val
                            ? val ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10' : 'border-[#FF1744] text-[#FF1744] bg-[#FF1744]/10'
                            : 'border-gray-700 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        {val ? 'Yes, unlocked' : 'No / Locked'}
                      </button>
                    ))}
                  </div>
                  {iCloudUnlocked === false && (
                    <p className="text-xs text-[#FF1744] mt-2">Parts-only value — we can still help you sign out remotely.</p>
                  )}
                </div>
              )}

              {needsCarrierCheck && (
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 mb-4">
                  <p className="text-sm font-semibold text-[#E0E0E0] mb-3">
                    Is it carrier unlocked and not blacklisted?
                  </p>
                  <div className="flex gap-3">
                    {[true, false].map(val => (
                      <button
                        key={String(val)}
                        onClick={() => setBlacklistClean(val)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                          blacklistClean === val
                            ? val ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10' : 'border-[#FF1744] text-[#FF1744] bg-[#FF1744]/10'
                            : 'border-gray-700 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        {val ? 'Yes, clean' : 'No / Blacklisted'}
                      </button>
                    ))}
                  </div>
                  {blacklistClean === false && (
                    <p className="text-xs text-[#FF1744] mt-2">Blacklisted devices are parts-only value.</p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setStep('model')}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={() => setStep('offer')}
                  disabled={!conditionComplete}
                  className="flex items-center gap-2 bg-[#39FF14] text-[#0D0D0D] px-6 py-3 rounded-xl font-bold text-sm hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  See My Offer <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Offer ─────────────────────────────────────────────── */}
          {step === 'offer' && selectedModel && selectedCondition && offer !== null && (
            <div>
              {/* Offer display */}
              <div className="text-center mb-8">
                <p className="text-[#B0B0B0] text-sm mb-1">Estimated offer for your</p>
                <p className="text-xl font-bold text-[#E0E0E0] mb-6">{selectedModel.name} · {selectedCondition.label}</p>
                <div
                  className="inline-flex items-baseline gap-2 px-10 py-6 rounded-3xl border-2 mb-4"
                  style={{ borderColor: selectedCondition.color, backgroundColor: `${selectedCondition.color}10` }}
                >
                  <span className="text-2xl font-bold" style={{ color: selectedCondition.color }}>up to</span>
                  <span className="text-7xl font-black" style={{ color: selectedCondition.color }}>${offer}</span>
                </div>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  Estimate only — final offer confirmed after inspection or photos. Prices subject to change based on current market.
                </p>
              </div>

              {/* Contact form */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#E0E0E0] mb-1">Lock in your quote</h3>
                <p className="text-sm text-[#B0B0B0] mb-5">Send us your info and we'll confirm within a few hours. Bring it in or ship it — your call.</p>

                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm placeholder-gray-500 focus:outline-none focus:border-[#00BFFF] transition-colors"
                  />
                  <input
                    type="text"
                    placeholder="Phone or email"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm placeholder-gray-500 focus:outline-none focus:border-[#00BFFF] transition-colors"
                  />
                  <textarea
                    placeholder="Anything else we should know? (optional)"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-[#E0E0E0] text-sm placeholder-gray-500 focus:outline-none focus:border-[#00BFFF] transition-colors resize-none"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <a
                    href={buildMailto()}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#39FF14] text-[#0D0D0D] px-6 py-3 rounded-xl font-bold text-sm hover:bg-white transition-colors"
                  >
                    <Send className="h-4 w-4" /> Email My Quote
                  </a>
                  <a
                    href={`sms:+19512168534&body=${encodeURIComponent(`Hi, I'd like to sell my ${selectedModel.name} (${selectedCondition.label}) — estimated offer $${offer}. Name: ${name}`)}`}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-800 border border-gray-700 text-[#E0E0E0] px-6 py-3 rounded-xl font-bold text-sm hover:border-[#00BFFF] transition-colors"
                  >
                    Text Us Instead
                  </a>
                </div>
              </div>

              {/* Summary strip */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center text-xs text-[#B0B0B0]">
                {[
                  'Working or broken accepted',
                  'Same-day cash / Zelle / Venmo',
                  'Ship or drop off',
                  'No obligation',
                ].map(item => (
                  <div key={item} className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-[#39FF14]" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-8">
                <button
                  onClick={() => setStep('condition')}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={reset}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#39FF14] transition-colors"
                >
                  <RotateCcw className="h-4 w-4" /> Start over
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What we accept strip */}
      <section className="py-12 bg-[#0D0D0D] border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0] mb-4">We also accept these with no quote needed</p>
          <p className="text-[#B0B0B0] text-sm max-w-2xl mx-auto">
            Sealed / new-in-box devices, accessories, controllers, games, charging cables, docks, and any gaming hardware not listed above.
            Just bring it in or shoot us a text — we'll make an offer on the spot.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <a
              href="tel:+17602147244"
              className="bg-gray-900 border border-gray-700 hover:border-[#39FF14] text-[#E0E0E0] px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
            >
              Call San Diego: (760) 214-7244
            </a>
            <a
              href="tel:+19512168534"
              className="bg-gray-900 border border-gray-700 hover:border-[#39FF14] text-[#E0E0E0] px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
            >
              Call Temecula: (951) 216-8534
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default BuybackPage;
